import { useState } from 'react';
import { AxiosError } from 'axios';
// mantine
import {
    Badge,
    Menu,
    Loader,
    Table,
    UnstyledButton,
    useMatches
} from '@mantine/core';
import { modals } from '@mantine/modals';
// icons
import { Ellipsis, Edit, Trash2, Play, Pause, Webhook } from 'lucide-react';
// components
import { PopoverButton } from '@newo/ui-kit';
import type { ISettingPreset, IConnector } from '@newo/api/gateway';
import EditConnectorForm from '@/pages/Integrations/components/EditConnectorForm';
import ConnectorSettingsForm from '@/pages/Integrations/components/ConnectorSettingsForm';
import WebhooksModalContent from '@/pages/Integrations/components/WebhooksModalContent';
// utils
import { openConfirmDeleteModal, openErrorModal } from '@/utils/ui.tsx';
import {
    useChangeConnectorSettingsMutation,
    useDeleteConnectorMutation,
    useLaunchConnectorMutation,
    useStopConnectorMutation
} from '@/services/integration/integration.queries.ts';
// assets
import styles from '@/pages/Integrations/components/ConnectorItem/ConnectorItem.module.css';

interface ConnectorItemProps {
    connector: IConnector;
    presets: ISettingPreset[];
    refetchConnectors: () => Promise<unknown>;
}
const HTTP_STATUS_UNPROCESSABLE_ENTITY = 422;

const ConnectorItem = ({
    connector,
    presets,
    refetchConnectors
}: ConnectorItemProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const changeSettingsMutation = useChangeConnectorSettingsMutation();
    const deleteMutation = useDeleteConnectorMutation();
    const launchMutation = useLaunchConnectorMutation();
    const stopMutation = useStopConnectorMutation();

    const modalSize = useMatches({
        xs: '90%',
        sm: '60%',
        md: '70%',
        lg: '50%',
        xl: '800px'
    });

    const handleConnectorSettingsSubmit = async (
        values: Record<string, unknown>
    ) =>
        new Promise((resolve, reject) => {
            const data = Object.entries(values).map(([idn, value]) => ({
                idn,
                value
            }));
            changeSettingsMutation.mutate(
                {
                    connectorId: connector.id,
                    data
                },
                {
                    onSuccess() {
                        refetchConnectors();
                        modals.closeAll();
                        resolve(true);
                    },
                    onError(error) {
                        reject(error);
                    }
                }
            );
        });

    const handleConnectorSettingsEdit = () => {
        modals.open({
            title: `Edit ${connector.title} Connector Settings`,
            size: modalSize,
            children: (
                <ConnectorSettingsForm
                    connectorId={connector.id}
                    presets={presets}
                    onCancel={() => {
                        modals.closeAll();
                    }}
                    onSubmit={handleConnectorSettingsSubmit}
                />
            )
        });
    };

    const handleWebhooks = () => {
        modals.open({
            title: `${connector.title} webhooks`,
            size: modalSize,
            children: (
                <WebhooksModalContent connectorIdn={connector.connector_idn} />
            )
        });
    };

    const handleConnectorEditSubmit = () => {
        refetchConnectors();
    };

    const handleDeleteConnector = () => {
        openConfirmDeleteModal({
            title: `Delete ${connector.title} connector?`,
            children: `Are you sure you want to delete ${connector.title} connector?`,
            confirmLabel: 'Delete connector',
            onConfirm: () => {
                deleteMutation.mutate(connector.id, {
                    onSuccess() {
                        refetchConnectors();
                    }
                });
            }
        });
    };

    const handleLaunchConnector = () => {
        setIsLoading(true);
        launchMutation.mutate(connector.id, {
            onSuccess: () => {
                void (async () => {
                    await refetchConnectors();
                    setIsLoading(false);
                })();
            },
            onError(error) {
                setIsLoading(false);
                if (
                    error instanceof AxiosError &&
                    error.response?.status === HTTP_STATUS_UNPROCESSABLE_ENTITY
                ) {
                    const reason =
                        (error.response.data as { reason?: string }).reason ??
                        '';
                    openErrorModal({
                        message: reason
                    });
                }
            }
        });
    };

    const handleStopConnector = () => {
        setIsLoading(true);
        stopMutation.mutate(connector.id, {
            onSuccess: () => {
                void (async () => {
                    await refetchConnectors();
                    setIsLoading(false);
                })();
            },
            onError: () => {
                setIsLoading(false);
            }
        });
    };

    return (
        <Table.Tr className={styles.connector}>
            <Table.Td colSpan={2}>
                <div className="grid grid-cols-[max-content_max-content_max-content] items-start gap-2 pl-8">
                    <div className="pt-0.5">↳</div>
                    <div>
                        {connector.title}
                        <div className="text-xs leading-none text-gray-6">
                            {connector.connector_idn}
                        </div>
                    </div>

                    <PopoverButton
                        className="pt-0.5 text-gray-6"
                        trigger={<Edit />}
                    >
                        {(close) => (
                            <EditConnectorForm
                                connector={connector}
                                onCancel={close}
                                onSubmit={handleConnectorEditSubmit}
                            />
                        )}
                    </PopoverButton>
                </div>
            </Table.Td>
            <Table.Td>
                {isLoading ? (
                    <Loader className="ml-6" size="xs" type="dots" />
                ) : undefined}
                {connector.status === 'running' && !isLoading && (
                    <Badge
                        className="capitalize"
                        color="green"
                        variant="filled"
                    >
                        Running
                    </Badge>
                )}
                {connector.status !== 'running' && !isLoading && (
                    <Badge className="capitalize" color="red" variant="filled">
                        Stopped
                    </Badge>
                )}
            </Table.Td>
            <Table.Td>
                <div className="flex justify-end gap-4">
                    <Menu shadow="md" trigger="click" width={150}>
                        <Menu.Target>
                            <UnstyledButton type="button">
                                <Ellipsis />
                            </UnstyledButton>
                        </Menu.Target>
                        <Menu.Dropdown>
                            {connector.status === 'running' ? (
                                <Menu.Item
                                    leftSection={<Pause />}
                                    onClick={handleStopConnector}
                                >
                                    Pause
                                </Menu.Item>
                            ) : (
                                <Menu.Item
                                    leftSection={<Play />}
                                    onClick={handleLaunchConnector}
                                >
                                    Run
                                </Menu.Item>
                            )}
                            {presets.length > 0 && (
                                <Menu.Item
                                    leftSection={<Edit />}
                                    onClick={handleConnectorSettingsEdit}
                                >
                                    Edit Settings
                                </Menu.Item>
                            )}
                            {connector.integration_idn === 'api' && (
                                <Menu.Item
                                    leftSection={<Webhook />}
                                    onClick={handleWebhooks}
                                >
                                    Webhooks
                                </Menu.Item>
                            )}
                            <Menu.Item
                                className="text-red-6"
                                leftSection={<Trash2 />}
                                onClick={handleDeleteConnector}
                            >
                                Remove
                            </Menu.Item>
                        </Menu.Dropdown>
                    </Menu>
                </div>
            </Table.Td>
        </Table.Tr>
    );
};

export default ConnectorItem;
